import {
  browserLocalPersistence,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  sendPasswordResetEmail,
  onAuthStateChanged,
  getAuth,
  type User as FirebaseUser,
} from 'firebase/auth';
import { firebaseAuth } from '@/infrastructure/firebase/firebaseAuth.js';
import { AuthService, type AuthUser, mapFirebaseUser, authServiceErrorMessage } from './AuthService.js';

export class FirebaseAuthService implements AuthService {
  private unsubscribe?: () => void;

  async initialize(): Promise<void> {
    const auth = firebaseAuth;
    if (!auth) throw new Error('Firebase Auth not initialized');
    await setPersistence(auth, browserLocalPersistence);
  }

  async signInWithEmail(email: string, password: string): Promise<AuthUser> {
    const auth = firebaseAuth;
    if (!auth) throw new Error('Firebase Auth not initialized');
    try {
      const credential = await signInWithEmailAndPassword(auth, email, password);
      return mapFirebaseUser(credential.user)!;
    } catch (error) {
      const code = (error as { code?: string }).code ?? 'unknown';
      throw new Error(authServiceErrorMessage(code));
    }
  }

  async signInWithGoogle(): Promise<AuthUser> {
    const auth = firebaseAuth;
    if (!auth) throw new Error('Firebase Auth not initialized');
    try {
      const provider = new GoogleAuthProvider();
      const credential = await signInWithPopup(auth, provider);
      return mapFirebaseUser(credential.user)!;
    } catch (error) {
      const code = (error as { code?: string }).code ?? 'unknown';
      throw new Error(authServiceErrorMessage(code));
    }
  }

  async signInAsMock(_role: 'ADMIN' | 'VIEWER'): Promise<AuthUser> {
    throw new Error('Mock sign-in is not available in Firebase mode');
  }

  async signOut(): Promise<void> {
    const auth = firebaseAuth;
    if (!auth) throw new Error('Firebase Auth not initialized');
    await signOut(auth);
  }

  getCurrentUser(): AuthUser | null {
    const auth = firebaseAuth;
    if (!auth) return null;
    return mapFirebaseUser(auth.currentUser);
  }

  subscribeAuthState(handler: (user: AuthUser | null) => void): () => void {
    const auth = firebaseAuth;
    if (!auth) {
      handler(null);
      return () => {};
    }
    this.unsubscribe = onAuthStateChanged(auth, (user: FirebaseUser | null) => {
      handler(mapFirebaseUser(user));
    });
    return this.unsubscribe;
  }

  async sendPasswordResetEmail(email: string): Promise<void> {
    const auth = firebaseAuth;
    if (!auth) throw new Error('Firebase Auth not initialized');
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      const code = (error as { code?: string }).code ?? 'unknown';
      throw new Error(authServiceErrorMessage(code));
    }
  }

  async getIdToken(): Promise<string | null> {
    const auth = firebaseAuth;
    if (!auth || !auth.currentUser) return null;
    return auth.currentUser.getIdToken();
  }

  async refreshCurrentUser(): Promise<AuthUser | null> {
    const auth = getAuth();
    if (!auth?.currentUser) return null;
    await auth.currentUser.reload();
    return mapFirebaseUser(auth.currentUser);
  }
}

export const createFirebaseAuthService = (): FirebaseAuthService => new FirebaseAuthService();
